/**
 * TEMP-H-011: Server-based Dashboard Store
 * 
 * Migrated dashboard store using server APIs and WebSocket query bridge
 * instead of direct Supabase access. Maintains same interface for compatibility.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { devtools } from 'zustand/middleware';
import type { 
  ExecutionStatus,
  ExecutionStatusType,
  ProgressUpdate, 
  CostUpdate, 
  LogEntry, 
  PipelineConfig,
  UINotification,
  StageMetrics,
  ChartDataPoint,
  QualityScore
} from '@/types/execution';
// Legacy WebSocket query bridge removed; realtime is handled by secure Socket.IO hooks
import { ServerIterationAPIService } from "@/services/iteration-api-server"

interface DashboardStore {
  // WebSocket Connection
  connectionStatus: 'connected' | 'disconnected' | 'reconnecting';
  lastConnectionUpdate: string;

  // Auth context
  currentUserId: string | null;
  
  // Executions
  executions: Map<string, ExecutionStatus>;
  activeExecutionId: string | null;
  
  // Polling state
  pollingInterval: NodeJS.Timeout | null;
  isPollingEnabled: boolean;
  
  // Real-time Data
  progressUpdates: Map<string, ProgressUpdate[]>; // executionId -> updates
  costUpdates: Map<string, CostUpdate[]>; // executionId -> updates  
  logEntries: Map<string, LogEntry[]>; // executionId -> logs
  qualityScores: Map<string, QualityScore[]>; // executionId -> quality scores
  
  // UI State
  notifications: UINotification[];
  isLoading: boolean;
  selectedStage: string | null;
  
  // Computed Data
  totalCostIncurred: number;
  avgQualityScore: number;
  activeExecutionsCount: number;
  
  // Actions
  initializeWebSocket: () => Promise<void>;
  disconnectWebSocket: () => void;
  fetchAndUpdateExecutionPortal: (executionId: string, reason?: string, stage?: string) => Promise<void>;
  
  // Database-first hydration
  hydrateFromDatabase: () => Promise<void>;
  subscribeToRealtimeUpdates: () => void;
  unsubscribeFromRealtimeUpdates: () => void;
  
  // Polling management
  startPolling: () => void;
  stopPolling: () => void;
  pollRunningExecutions: () => Promise<void>;
  
  // Execution Management
  startExecution: (config: PipelineConfig) => void;
  startNewExecution: (config: PipelineConfig) => void;
  pauseExecution: (executionId: string) => void;
  resumeExecution: (executionId: string) => void;
  stopExecution: (executionId: string) => void;
  duplicateExecution: (executionId: string) => Promise<string | null>;
  softDeleteExecution: (executionId: string) => Promise<void>;
  undoDeleteExecution: () => Promise<void>;
  setActiveExecution: (executionId: string | null) => void;
  subscribeToExecution: (executionId: string) => void;
  unsubscribeFromExecution: (executionId: string) => void;
  
  // Data Updates
  updateExecutionStatus: (status: ExecutionStatus) => void;
  addProgressUpdate: (update: ProgressUpdate) => void;
  addCostUpdate: (update: CostUpdate) => void;
  addLogEntry: (entry: LogEntry) => void;
  addQualityScore: (score: QualityScore) => void;
  setProgressHistory: (executionId: string, updates: ProgressUpdate[]) => void;
  setCostHistory: (executionId: string, updates: CostUpdate[]) => void;
  setQualityScoreHistory: (executionId: string, scores: QualityScore[]) => void;
  
  // UI Actions
  addNotification: (notification: Omit<UINotification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  setLoading: (loading: boolean) => void;
  setSelectedStage: (stage: string | null) => void;
  
  // Computed Getters
  getActiveExecution: () => ExecutionStatus | null;
  getExecutionProgressHistory: (executionId: string) => ProgressUpdate[];
  getExecutionCostHistory: (executionId: string) => CostUpdate[];
  getExecutionLogs: (executionId: string) => LogEntry[];
  getExecutionQualityScores: (executionId: string) => QualityScore[];
  getStageMetrics: (executionId: string) => StageMetrics[];
  getCostChartData: (executionId: string) => ChartDataPoint[];
  getQualityChartData: (executionId: string) => ChartDataPoint[];
}

// WebSocket subscription management (legacy, unused)
// let wsSubscriptions: string[] = [];

export const useServerDashboardStore = create<DashboardStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial State
      connectionStatus: 'disconnected',
      lastConnectionUpdate: new Date().toISOString(),
      currentUserId: null,
      executions: new Map(),
      activeExecutionId: null,
      pollingInterval: null,
      isPollingEnabled: false,
      progressUpdates: new Map(),
      costUpdates: new Map(),
      logEntries: new Map(),
      qualityScores: new Map(),
      notifications: [],
      isLoading: false,
      selectedStage: null,
      totalCostIncurred: 0,
      avgQualityScore: 0,
      activeExecutionsCount: 0,

      // WebSocket Management
      initializeWebSocket: async () => {
        // No-op: realtime handled per-page via secure Socket.IO bridge
        set({ isLoading: false });
      },

      disconnectWebSocket: () => {
        // No-op for legacy bridge; stop polling and mark disconnected
        // Stop polling
        get().stopPolling();
        set({ 
          connectionStatus: 'disconnected',
          lastConnectionUpdate: new Date().toISOString()
        });
        console.log('🔌 Server Dashboard realtime disabled (legacy bridge removed)');
      },

      // Database-first hydration - load recent executions from server API
      hydrateFromDatabase: async () => {
        try {
          set({ isLoading: true });
          
          console.log('📊 Starting database hydration via server API...');
          
          // HISI-FE-AUTH-01: Cookie-based auth, no token checks needed
          
          // Fetch recent executions via server API
          const { executions: executionRows } = await ServerIterationAPIService.getExecutions({
            limit: 50
          });
          
          // Convert to ExecutionStatus objects
          const executionMap = new Map<string, ExecutionStatus>();
          let totalCost = 0;
          let qualityScores: number[] = [];
          let activeCount = 0;
          
          executionRows?.forEach((row) => {
            const execution: ExecutionStatus = {
              id: row.id,
              status: row.status as ExecutionStatusType,
              current_stage: row.current_stage,
              progress: row.progress || {
                overall: 0,
                current_stage: 0,
                stages_completed: 0,
                total_stages: 0
              },
              // Performance Metrics
              elapsed_time: 0,
              estimated_remaining: 0,
              cost_incurred: row.actual_cost_usd || 0,
              cost_projected: row.total_estimated_cost_usd || 0,
              actual_cost_usd: row.actual_cost_usd || 0,
              // Quality Metrics
              avg_quality_score: 0,
              frames_passed_target: 0,
              frames_passed_fallback: 0,
              strategy_adaptations: 0,
              // Optional metadata
              metadata: row.metadata || {},
              config: row.config || {},
              created_at: row.created_at,
              updated_at: row.updated_at,
            };
            
            executionMap.set(execution.id, execution);
            totalCost += execution.cost_incurred;
            
            // Count active executions
            const activeStatuses = ['running', 'processing', 'pending', 'queued'];
            if (activeStatuses.includes(execution.status)) {
              activeCount++;
            }
          });
          
          // Calculate average quality score (mock for now)
          const avgQuality = qualityScores.length > 0 
            ? qualityScores.reduce((sum, score) => sum + score, 0) / qualityScores.length 
            : 0;
          
          set({
            executions: executionMap,
            totalCostIncurred: totalCost,
            avgQualityScore: avgQuality,
            activeExecutionsCount: activeCount,
          });
          
          console.log(`📊 Hydrated ${executionMap.size} executions from server API`);
        } catch (error) {
          console.error('Failed to hydrate from database:', error);
          
          get().addNotification({
            type: 'error',
            title: 'Data Load Failed',
            message: 'Failed to load execution data from server',
            duration: 5000
          });
        } finally {
          set({ isLoading: false });
        }
      },

      // Subscribe to WebSocket realtime updates
      subscribeToRealtimeUpdates: () => {
        // No-op: page-level hooks manage secure Socket.IO subscriptions
      },

      unsubscribeFromRealtimeUpdates: () => {
        // No-op with legacy bridge removed
        console.log('📡 Realtime subscriptions cleared (legacy bridge removed)');
      },

      // Fetch and update execution details
      fetchAndUpdateExecutionPortal: async (executionId: string, reason?: string, _stage?: string) => {
        try {
          console.log(`🔄 Refreshing execution ${executionId} (${reason || 'manual'})`);
          
          const details = await ServerIterationAPIService.getExecutionDetails(executionId);
          
          if (details.execution) {
            const execution: ExecutionStatus = {
              id: details.execution.id,
              status: details.execution.status as ExecutionStatusType,
              current_stage: details.execution.current_stage,
              progress: details.execution.progress || {
                overall: 0,
                current_stage: 0,
                stages_completed: 0,
                total_stages: 0
              },
              // Performance Metrics
              elapsed_time: 0,
              estimated_remaining: 0,
              cost_incurred: details.execution.actual_cost_usd || 0,
              cost_projected: details.execution.total_estimated_cost_usd || 0,
              actual_cost_usd: details.execution.actual_cost_usd || 0,
              // Quality Metrics
              avg_quality_score: 0,
              frames_passed_target: 0,
              frames_passed_fallback: 0,
              strategy_adaptations: 0,
              // Optional metadata
              metadata: details.execution.metadata || {},
              config: details.execution.config || {},
              created_at: details.execution.created_at,
              updated_at: details.execution.updated_at,
            };
            
            get().updateExecutionStatus(execution);
          }
        } catch (error) {
          console.error(`Failed to refresh execution ${executionId}:`, error);
        }
      },

      // Polling management
      startPolling: () => {
        if (get().pollingInterval || !get().isPollingEnabled) return;
        
        const interval = setInterval(() => {
          get().pollRunningExecutions();
        }, 30000); // Poll every 30 seconds
        
        set({ pollingInterval: interval });
        console.log('🔄 Started defensive polling');
      },

      stopPolling: () => {
        const interval = get().pollingInterval;
        if (interval) {
          clearInterval(interval);
          set({ pollingInterval: null });
          console.log('⏹️ Stopped defensive polling');
        }
      },

      pollRunningExecutions: async () => {
        try {
          const executions = get().executions;
          const runningExecutions = Array.from(executions.values()).filter(
            exec => ['running', 'processing', 'pending'].includes(exec.status)
          );
          
          if (runningExecutions.length === 0) return;
          
          // Refresh running executions
          for (const execution of runningExecutions) {
            await get().fetchAndUpdateExecutionPortal(execution.id, 'polling');
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
      },

      // Execution Management (delegate to server API)
      startExecution: (config: PipelineConfig) => {
        get().startNewExecution(config);
      },

      startNewExecution: async (config: PipelineConfig) => {
        try {
          const result = await ServerIterationAPIService.createExecution({
            stages: config.stages || [],
            settings: config.settings || {},
          });
          
          if (result.execution_id) {
            get().addNotification({
              type: 'success',
              title: 'Execution Started',
              message: `Pipeline execution started successfully`,
              duration: 5000
            });
            
            // Refresh data to show new execution
            await get().hydrateFromDatabase();
          }
        } catch (error) {
          console.error('Failed to start execution:', error);
          get().addNotification({
            type: 'error',
            title: 'Execution Failed',
            message: 'Failed to start pipeline execution',
            duration: 5000
          });
        }
      },

      pauseExecution: async (executionId: string) => {
        try {
          await ServerIterationAPIService.controlExecution(executionId, 'pause');
          get().fetchAndUpdateExecutionPortal(executionId, 'pause');
        } catch (error) {
          console.error('Failed to pause execution:', error);
        }
      },

      resumeExecution: async (executionId: string) => {
        try {
          await ServerIterationAPIService.controlExecution(executionId, 'resume');
          get().fetchAndUpdateExecutionPortal(executionId, 'resume');
        } catch (error) {
          console.error('Failed to resume execution:', error);
        }
      },

      stopExecution: async (executionId: string) => {
        try {
          await ServerIterationAPIService.controlExecution(executionId, 'stop');
          get().fetchAndUpdateExecutionPortal(executionId, 'stop');
        } catch (error) {
          console.error('Failed to stop execution:', error);
        }
      },

      duplicateExecution: async (_executionId: string): Promise<string | null> => {
        try {
          // TODO: Implement duplication via server API
          get().addNotification({
            type: 'info',
            title: 'Feature Coming Soon',
            message: 'Execution duplication will be available soon',
            duration: 3000
          });
          return null;
        } catch (error) {
          console.error('Failed to duplicate execution:', error);
          return null;
        }
      },

      softDeleteExecution: async (_executionId: string): Promise<void> => {
        try {
          // TODO: Implement soft delete via server API
          get().addNotification({
            type: 'info',
            title: 'Feature Coming Soon',
            message: 'Execution deletion will be available soon',
            duration: 3000
          });
        } catch (error) {
          console.error('Failed to delete execution:', error);
        }
      },

      undoDeleteExecution: async (): Promise<void> => {
        try {
          // TODO: Implement undo delete via server API
          get().addNotification({
            type: 'info',
            title: 'Feature Coming Soon',
            message: 'Undo delete will be available soon',
            duration: 3000
          });
        } catch (error) {
          console.error('Failed to undo delete:', error);
        }
      },

      setActiveExecution: (executionId: string | null) => {
        set({ activeExecutionId: executionId });
      },

      subscribeToExecution: (executionId: string) => {
        // Already handled by global subscriptions
        console.log(`📡 Subscribed to execution ${executionId}`);
      },

      unsubscribeFromExecution: (executionId: string) => {
        // Already handled by global subscriptions
        console.log(`📡 Unsubscribed from execution ${executionId}`);
      },

      // Data Updates
      updateExecutionStatus: (status: ExecutionStatus) => {
        set((state) => {
          const executions = new Map(state.executions);
          executions.set(status.id, status);
          
          // Recalculate metrics
          const activeStatuses = ['running', 'processing', 'pending', 'queued'];
          const activeExecutionsCount = Array.from(executions.values()).filter(
            (exec) => activeStatuses.includes(exec.status)
          ).length;
          
          const totalCostIncurred = Array.from(executions.values()).reduce(
            (sum, exec) => sum + (exec.cost_incurred || 0), 0
          );
          
          return { executions, activeExecutionsCount, totalCostIncurred };
        });
      },

      addProgressUpdate: (update: ProgressUpdate) => {
        set((state) => {
          const progressUpdates = new Map(state.progressUpdates);
          const updates = progressUpdates.get(update.execution_id) || [];
          updates.push(update);
          progressUpdates.set(update.execution_id, updates);
          return { progressUpdates };
        });
      },

      addCostUpdate: (update: CostUpdate) => {
        set((state) => {
          const costUpdates = new Map(state.costUpdates);
          const updates = costUpdates.get(update.execution_id) || [];
          updates.push(update);
          costUpdates.set(update.execution_id, updates);
          return { costUpdates };
        });
      },

      addLogEntry: (entry: LogEntry) => {
        set((state) => {
          const logEntries = new Map(state.logEntries);
          const entries = logEntries.get(entry.execution_id) || [];
          entries.push(entry);
          logEntries.set(entry.execution_id, entries);
          return { logEntries };
        });
      },

      addQualityScore: (score: QualityScore) => {
        set((state) => {
          const qualityScores = new Map(state.qualityScores);
          const scores = qualityScores.get(score.execution_id) || [];
          scores.push(score);
          qualityScores.set(score.execution_id, scores);
          return { qualityScores };
        });
      },

      setProgressHistory: (executionId: string, updates: ProgressUpdate[]) => {
        set((state) => {
          const progressUpdates = new Map(state.progressUpdates);
          progressUpdates.set(executionId, updates);
          return { progressUpdates };
        });
      },

      setCostHistory: (executionId: string, updates: CostUpdate[]) => {
        set((state) => {
          const costUpdates = new Map(state.costUpdates);
          costUpdates.set(executionId, updates);
          return { costUpdates };
        });
      },

      setQualityScoreHistory: (executionId: string, scores: QualityScore[]) => {
        set((state) => {
          const qualityScores = new Map(state.qualityScores);
          qualityScores.set(executionId, scores);
          return { qualityScores };
        });
      },

      // UI Actions
      addNotification: (notification: Omit<UINotification, 'id' | 'timestamp'>) => {
        const id = Math.random().toString(36).substring(2, 9);
        const timestamp = new Date().toISOString();
        
        set((state) => ({
          notifications: [...state.notifications, { id, timestamp, ...notification }]
        }));
        
        // Auto-remove after duration
        if (notification.duration && notification.duration > 0) {
          setTimeout(() => {
            get().removeNotification(id);
          }, notification.duration);
        }
      },

      removeNotification: (id: string) => {
        set((state) => ({
          notifications: state.notifications.filter(n => n.id !== id)
        }));
      },

      clearNotifications: () => {
        set({ notifications: [] });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setSelectedStage: (stage: string | null) => {
        set({ selectedStage: stage });
      },

      // Computed Getters
      getActiveExecution: () => {
        const { executions, activeExecutionId } = get();
        return activeExecutionId ? executions.get(activeExecutionId) || null : null;
      },

      getExecutionProgressHistory: (executionId: string) => {
        return get().progressUpdates.get(executionId) || [];
      },

      getExecutionCostHistory: (executionId: string) => {
        return get().costUpdates.get(executionId) || [];
      },

      getExecutionLogs: (executionId: string) => {
        return get().logEntries.get(executionId) || [];
      },

      getExecutionQualityScores: (executionId: string) => {
        return get().qualityScores.get(executionId) || [];
      },

      getStageMetrics: (_executionId: string): StageMetrics[] => {
        // TODO: Implement stage metrics calculation
        return [];
      },

      getCostChartData: (executionId: string): ChartDataPoint[] => {
        const costHistory = get().getExecutionCostHistory(executionId);
        return costHistory.map(update => ({
          timestamp: update.timestamp,
          value: update.session_total || 0
        }));
      },

      getQualityChartData: (executionId: string): ChartDataPoint[] => {
        const qualityHistory = get().getExecutionQualityScores(executionId);
        return qualityHistory.map(score => ({
          timestamp: score.timestamp,
          value: score.score
        }));
      },
    })),
    {
      name: 'dashboard-server-store',
    }
  )
);

// Export both names for compatibility
export const useDashboardStore = useServerDashboardStore;
export const useDashboardServerStore = useServerDashboardStore;